import React, { useMemo, useState } from "react";
import {
  Modal,
  Grid,
  Column,
  FormGroup,
  TextInput,
  Dropdown,
  TextArea,
  Loading,
  Tile,
  Stack,
  Button,
  InlineLoading,
  InlineNotification,
  MultiSelect as CarbonMultiSelect,
  Tag,
} from "@carbon/react";
import { Document, Download, View, TrashCan, Close } from "@carbon/icons-react";

export default function PropertyVerifyModal({
  isVerifyModalOpen,
  verifyProperty,
  verifyForm,
  setVerifyForm,
  dropdownOptions,
  propertyDocuments,
  loadingDocuments,
  verifyingId,
  handleVerifyConfirmed,
  setIsVerifyModalOpen,
  handleDownloadDocument,
  handleViewDocument,
  apiError,
  setApiError,
  onRemovePlan, 
  //formatDate
}) {

  const [pendingRemove, setPendingRemove] = useState(null);
  const [removingPlan, setRemovingPlan] = useState(false);

  const requiredLabel = (text) => (
    <>
      {text} <span style={{ color: "#da1e28" }}>*</span>
    </>
  );

  const tagItems = useMemo(() => {
  const base = dropdownOptions.tags || [];
  const selected = verifyForm.tags || [];
  const existingKeys = new Set(
    base.map((t) => String(t.id ?? t.text))
  );
  const missing = selected.filter(
    (t) => !existingKeys.has(String(t.id ?? t.text))
  );
  return [...base, ...missing];
}, [dropdownOptions.tags, verifyForm.tags]);

  // ✅ NEW: property type flags
  const isSaleProp =
    verifyProperty?.postedFor === "sale" ||
    verifyProperty?.postedFor === "resale";

  const isRentProp =
    verifyProperty?.postedFor === "rent" ||
    verifyProperty?.postedFor === "pg/hostel" ||
    verifyProperty?.postedFor === "flatmates";

  // ✅ NEW: price is mandatory for verification
  const isPriceProvided = useMemo(() => {
    if (isSaleProp) {
      return (
        verifyForm.startPrice &&
        verifyForm.startPrice.toString().trim() !== "" &&
        Number(verifyForm.startPrice) > 0
      );
    }
    if (isRentProp) {
      return (
        verifyForm.price &&
        verifyForm.price.toString().trim() !== "" &&
        Number(verifyForm.price) > 0
      );
    }
    return true; // unknown type — don't block
  }, [isSaleProp, isRentProp, verifyForm.startPrice, verifyForm.price]);

  const confirmRemovePlan = async () => {
    if (!pendingRemove) return;
    setRemovingPlan(true);
    try {
      await onRemovePlan(pendingRemove);
    } finally {
      setRemovingPlan(false);
      setPendingRemove(null);
    }
  };

  return (
    <>
    <Modal
      open={isVerifyModalOpen}
      modalHeading={`Verify Property - ${verifyProperty?.propertyTitle || ""}`}
      primaryButtonText={verifyingId ? "" : "Submit"}
      secondaryButtonText="Cancel"
      primaryButtonDisabled={!!verifyingId || !isPriceProvided}   // ✅ CHANGED
onRequestClose={() => {
  if (!verifyingId) {
    setIsVerifyModalOpen(false);
    setApiError("");
  }
}}
      onRequestSubmit={() => {
        if (verifyingId) return;
        if (!isPriceProvided) return;   // ✅ NEW: block if no price
        handleVerifyConfirmed();
      }}
      size="md"
    >

      {/* MOVE THE ENTIRE VERIFY MODAL UI HERE */}

      {console.log("DEBUG verifyProperty:", verifyProperty)}
      
      {verifyProperty && (
        <div>
          <Grid narrow>
            <Column lg={16}>
            {/* ✅ NEW: price required warning */}
{!isPriceProvided && (
  <InlineNotification
    kind="error"
    title="Price required"
    subtitle={
      isSaleProp
        ? "Please enter the Price before verifying this property."
        : "Please enter the Rental Amount before verifying this property."
    }
    lowContrast
    hideCloseButton
    style={{ marginBottom: "1rem" }}
  />
)}
{verifyProperty.existingSubscriptions?.length > 0 && (
  <Tile
    style={{
      marginBottom: "1rem",
      backgroundColor: "#edf5ff",
      border: "1px solid #a6c8ff",
    }}
  >
    <strong style={{ display: "block", marginBottom: "0.5rem" }}>
      Plan(s) already added:
    </strong>

    <Stack gap={3}>
{verifyProperty.existingSubscriptions
  .filter(
    (sub) =>
      !["cancelled", "canceled"].includes(
        (sub.subscription_status || sub.status || "").toLowerCase()
      )
  )
  .map((sub) => (
          <div
            key={sub.id || sub.subscription_plan_id}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "0.5rem 0.75rem",
              background: "#ffffff",
              border: "1px solid #d0e2ff",
              borderRadius: "4px",
            }}
          >
            <div>
              <div style={{ fontWeight: 600, fontSize: "0.875rem" }}>
                {sub.plan_name || "Unnamed Plan"}
              </div>
              {sub.amount != null && (
                <div style={{ fontSize: "0.75rem", color: "#6f6f6f" }}>
                  ₹ {Number(sub.amount).toLocaleString("en-IN")} ·{" "}
                  {sub.currency || "INR"}
                </div>
              )}
            </div>

                                    <Button
                              kind="danger--ghost"
                              size="sm"
                              renderIcon={TrashCan}
                              iconDescription="Remove plan"
                              onClick={() => {
                                setApiError("");
                                setPendingRemove(sub);
                              }}
                              disabled={!!verifyingId || removingPlan}
                            >
                              Remove
                            </Button>
                          </div>
                        ))}
                    </Stack>
                  </Tile>
                )}
              <Tile
                style={{
                  marginBottom: "1rem",
                  backgroundColor: "#fff8e1",
                  border: "1px solid #ffe58f",
                }}
              >
                <div style={{ marginBottom: "0.75rem" }}>
                  {verifyProperty.hasExistingPlan
                    ? "Select an additional plan to add, if needed."
                    : "No subscription plan attached yet. Select one below so this property expires correctly."}
                </div>
                <Dropdown
  id="verify-plan-select"
  titleText="Subscription Plan"
  label="Select a plan"
  items={(dropdownOptions.subscriptionPlans || []).filter((plan) => {
    // 1. Hide plans already attached to this property
    const existingIds = (verifyProperty.existingPlanIds || []).map((id) =>
      String(id)
    );
    if (existingIds.includes(String(plan.id))) return false;

    // 2. Filter by property's posted-for type
    const postedFor = (verifyProperty?.postedFor || "").toLowerCase();
    const isRent = ["rent", "pg/hostel", "flatmates"].includes(postedFor);
    const isSale = ["sale", "resale"].includes(postedFor);

    // Normalize the plan's type. Accept common variants.
    const rawType =
      plan.planType ??           // from your normalizer
      plan.plan_type ??
      plan.PLAN_TYPE ??
      plan.type ??
      "";
    const planType = String(rawType).toLowerCase().replace(/[\s_-]+/g, "");

    // Plans that apply to both rent and sale
    const isGeneric =
      !planType ||
      ["all", "general", "both", "any"].includes(planType);

    if (isGeneric) return true;

    const isRentPlan =
      planType.includes("rent") ||
      planType.includes("rental") ||
      planType.includes("pg") ||
      planType.includes("flatmate");

    const isSalePlan =
      planType.includes("sale") || planType.includes("resale");

    if (isRent && isRentPlan) return true;
    if (isSale && isSalePlan) return true;

    return false;
  })}
  itemToString={(i) => (i?.name ? `${i.name} (${i.durationDays} days)` : "")}
  selectedItem={verifyForm.selectedPlan}
  onChange={({ selectedItem }) => {
    setApiError("");
    setVerifyForm((p) => ({ ...p, selectedPlan: selectedItem }));
  }}
/>
                
               {apiError && (
  <InlineNotification
    kind="warning"
    title="Subscription Plan Information"
    subtitle={apiError}
    lowContrast
    hideCloseButton
    style={{ marginTop: "1rem" }}
  />
)}

              </Tile>
            </Column>

              {/* LEFT COLUMN - VERIFY FORM */}
              <Column lg={7} md={4} sm={4}>
                <FormGroup>
                  {(() => {
                    const isSale =
    verifyProperty?.postedFor === "sale" ||
    verifyProperty?.postedFor === "resale";

  const isRent =
    verifyProperty?.postedFor === "rent" ||
    verifyProperty?.postedFor === "pg/hostel" ||
    verifyProperty?.postedFor === "flatmates";


                    return (
                      <>
                        {/* SALE - Starting Price and Unit on same line */}
                        {isSale && (
                          <>
                            <Grid narrow>
                              {/* Starting Price and Unit in same row */}
                              <Column lg={8}>
                                <div>
                                                                       <TextInput
                                      id="start-price"
                                      labelText={requiredLabel(" Price (₹) ")}
                                      value={verifyForm.startPrice}
                                      onChange={(e) =>
                                        setVerifyForm(p => ({
                                          ...p,
                                          startPrice: e.target.value.replace(/[^0-9]/g, "")
                                        }))
                                      }
                                      invalid={!verifyForm.startPrice}                                   // ✅ NEW
                                      invalidText="Price is required to verify this property"           // ✅ NEW
                                      placeholder="Enter price"                                         // ✅ NEW
                                    />
                                  </div>
                              
                                  
                              </Column>
                            </Grid>

                            {/* Ending Price - Below the starting price row */}
                           {/*<Grid narrow style={{ marginTop: 12 }}>
                              <Column lg={8}>
                                <TextInput
                                  id="end-price"
                                  labelText={requiredLabel("Ending Price (₹) ")}
                                  value={verifyForm.endPrice}
                                  onChange={(e) =>
                                    setVerifyForm(p => ({
                                      ...p,
                                      endPrice: e.target.value.replace(/[^0-9]/g, "")
                                    }))
                                  }
                                />
                              </Column>
                            </Grid>*/}
                          </>
                        )}

                        {/* RENT - Just rental amount */}
                        {isRent && (
                          <Grid narrow>
                            <Column lg={8}>
                                                          <TextInput
                                id="rent"
                                labelText={requiredLabel("Rental Amount (₹) ")}
                                value={verifyForm.price}
                                onChange={(e) =>
                                  setVerifyForm(p => ({
                                    ...p,
                                    price: e.target.value.replace(/[^0-9]/g, "")
                                  }))
                                }
                                invalid={!verifyForm.price}                                              // ✅ NEW
                                invalidText="Rental amount is required to verify this property"          // ✅ NEW
                                placeholder="Enter rental amount"                                        // ✅ NEW
                              />
                            </Column>
                          </Grid>
                        )}

                        {/* VERIFICATION STATUS */}
                        <Grid narrow style={{ marginTop: 16 }}>
                          <Column lg={8}>
                            <Dropdown
                              titleText="Verification Status"
                              label="Select"
                              items={dropdownOptions.verificationStatus}
                              itemToString={(i) => i?.text || ""}
                              selectedItem={dropdownOptions.verificationStatus.find(
                                i => i.text === verifyForm.verificationStatus
                              )}
                              onChange={({ selectedItem }) =>
                                setVerifyForm(p => ({
                                  ...p,
                                  verificationStatus: selectedItem?.text || ""
                                }))
                              }
                            />
                          </Column>
                        </Grid>

                        {/* TAGS - Now pre-populated with matching tags */}
                        <Grid narrow style={{ marginTop: 16 }}>
                          <Column lg={8}>
<CarbonMultiSelect
  id="tags-select"
  titleText="Tags"
  items={tagItems}
  itemToString={(i) => i?.text || ""}
  selectedItems={verifyForm.tags}
  compareItems={(a, b) => {
    if (!a || !b) return false;
    // Prefer id, fall back to text
    if (a.id != null && b.id != null) return String(a.id) === String(b.id);
    return a.text === b.text;
  }}
  onChange={({ selectedItems }) =>
    setVerifyForm(p => ({
      ...p,
      tags: selectedItems
    }))
  }
/>
                          </Column>

                          <Column lg={8}>
                            <TextArea
                              id="comments"
                              labelText="Comments"
                              placeholder="Enter comments"
                              rows={4}
                              value={verifyForm.comments || ""} 
                              onChange={(e) =>
                                setVerifyForm(p => ({
                                  ...p,
                                  comments: e.target.value
                                }))
                              }
                            />
                          </Column>
                        </Grid>
                      </>
                    );
                  })()}
                </FormGroup>
              </Column>

{/* RIGHT COLUMN - DOCUMENTS */}
{!loadingDocuments && propertyDocuments?.length > 0 && (
  <Column lg={8} md={4} sm={4}>
    <div
      style={{
        backgroundColor: "#f4f4f4",
        borderRadius: "8px",
        padding: "1rem",
        height: "100%",
        border: "1px solid #e0e0e0",
      }}
    >
      <h4
        style={{
          marginBottom: "1rem",
          fontSize: "1rem",
          fontWeight: "600",
          color: "#161616",
        }}
      >
        Uploaded Documents
      </h4>

      <div style={{ width: "100%" }}>
        <Stack gap={2}>
          {propertyDocuments.map((doc) => (
            <Tile
              key={doc.id}
              style={{
                padding: "0.75rem",
                backgroundColor: "#fff",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      marginBottom: "0.25rem",
                    }}
                  >
                    <Document size={16} />

                    <strong style={{ fontSize: "0.875rem" }}>
                      {doc.name}
                    </strong>
                  </div>

                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: "#666",
                    }}
                  >
                    {doc.type} • {doc.date}
                  </div>
                </div>

                <div
                  style={{
                    display: "flex",
                    gap: "0.25rem",
                  }}
                >
                  <Button
                    size="sm"
                    kind="ghost"
                    hasIconOnly
                    renderIcon={View}
                    iconDescription="View"
                    onClick={() => handleViewDocument(doc.url)}
                    style={{ minWidth: "32px" }}
                  />

                  <Button
                    size="sm"
                    kind="ghost"
                    hasIconOnly
                    renderIcon={Download}
                    iconDescription="Download"
                    onClick={() => handleDownloadDocument(doc.url)}
                    style={{ minWidth: "32px" }}
                  />
                </div>
              </div>
            </Tile>
          ))}
        </Stack>
      </div>
    </div>
  </Column>
)}
            </Grid>

            {verifyingId && (
              <div style={{ 
                display: "flex", 
                alignItems: "center", 
                gap: 12, 
                marginTop: 16,
                padding: '1rem',
                backgroundColor: '#f4f4f4',
                borderRadius: '4px'
              }}>
                <InlineLoading  description="Submitting..." />
                <span>Submitting verification...</span>
              </div>
            )}
          </div>
        )}
      </Modal>

            <Modal
        open={!!pendingRemove}
        danger
        modalHeading="Confirm Remove Plan"
        primaryButtonText={removingPlan ? "" : "Remove"}
        secondaryButtonText="Cancel"
        primaryButtonDisabled={removingPlan}
        onRequestClose={() => {
          if (!removingPlan) {
            setPendingRemove(null);
            setApiError("");
          }
        }}
        onRequestSubmit={confirmRemovePlan}
        size="sm"
      >
        <p style={{ marginBottom: "1rem" }}>
          Are you sure you want to remove{" "}
          <strong>{pendingRemove?.plan_name || "this plan"}</strong> from this
          property? This will cancel the subscription.
        </p>

        {pendingRemove?.amount != null && (
          <p style={{ marginBottom: "1rem", fontSize: "0.875rem", color: "#525252" }}>
            Amount: ₹ {Number(pendingRemove.amount).toLocaleString("en-IN")} ·{" "}
            {pendingRemove.currency || "INR"}
          </p>
        )}

        {removingPlan && (
          <InlineLoading description="Removing plan..." />
        )}
      </Modal>

      </>
  

  );
}